import apiClient from '../apiClient';

/** Minimal patient shape the backend joins onto a referral row in list views. */
export interface ReferralPatient {
  id: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  phone?: string | null;
}

export interface RecordInboundReferralDto {
  patientId: string;
  referredFromName: string;
  referredFromFacility?: string;
  referredFromContact?: string;
  specialty: string;
  reason: string;
  receivedOn?: string;
  addressedToId?: string;
  letterAttachmentId?: string;
}

export interface Referral {
  id: string;
  patientId: string;
  medicalRecordId: string | null;
  referredBy: string;
  /** Who signed it — three fields, joined by the list and the read. */
  referrer?: { id: string; firstName?: string | null; lastName?: string | null } | null;
  specialty: string;
  reason: string;
  clinicalContext: string | null;
  priority: 'routine' | 'urgent' | 'stat';
  status: 'draft' | 'signed' | 'sent' | 'returned' | 'cancelled' | 'received';
  /** Outbound = this clinic sent it; inbound = a patient arrived with it. */
  direction?: 'outbound' | 'inbound';
  /** The EXTERNAL referrer of an inbound referral — not a user here. */
  referredFromName?: string | null;
  referredFromFacility?: string | null;
  referredFromContact?: string | null;
  /** When the letter reached this clinic (YYYY-MM-DD). */
  receivedOn?: string | null;
  letterAttachmentId?: string | null;
  // Optional addressee fields.
  referredToName: string | null;
  referredToFacility: string | null;
  referredToContact: string | null;
  neededBy: string | null;
  electronicSignature: {
    signedBy: string;
    signedAt: string;
    signatureHash: string;
    ipAddress: string;
  } | null;
  responseNote: string | null;
  respondedAt: string | null;
  cancelledReason: string | null;
  createdAt: string;
  /** Present on list/detail responses (backend leftJoins the patient). */
  patient?: ReferralPatient | null;
}

export interface CreateReferralDto {
  patientId: string;
  medicalRecordId?: string;
  specialty: string;
  reason: string;
  clinicalContext?: string;
  priority?: 'routine' | 'urgent' | 'stat';
  referredToName?: string;
  referredToFacility?: string;
  referredToContact?: string;
  neededBy?: string;
}

export type UpdateReferralDto = Partial<
  Omit<CreateReferralDto, 'patientId' | 'medicalRecordId'>
>;

export interface ReferralListFilters {
  status?: Referral['status'];
  patientId?: string;
}

// Backend wraps responses in {success,data}; unwrap at the caller.
export const referralsApi = {
  create: async (dto: CreateReferralDto): Promise<Referral> => {
    const res = await apiClient.post('/referrals', dto);
    return res.data.data;
  },
  /** Clinic-wide list (self-scoped server-side: doctors see own, managers all). */
  list: async (filters: ReferralListFilters = {}): Promise<Referral[]> => {
    const res = await apiClient.get('/referrals', { params: filters });
    return res.data.data;
  },
  update: async (id: string, dto: UpdateReferralDto): Promise<Referral> => {
    const res = await apiClient.patch(`/referrals/${id}`, dto);
    return res.data.data;
  },
  /** A patient arrived holding another doctor's letter. */
  recordInbound: async (dto: RecordInboundReferralDto): Promise<Referral> => {
    const response = await apiClient.post('/referrals/inbound', dto);
    return (response.data?.data ?? response.data) as Referral;
  },

  getByPatient: async (patientId: string): Promise<Referral[]> => {
    const res = await apiClient.get(`/referrals/patient/${patientId}`);
    return res.data.data;
  },
  getById: async (id: string): Promise<Referral> => {
    const res = await apiClient.get(`/referrals/${id}`);
    return res.data.data;
  },
  sign: async (id: string): Promise<Referral> => {
    const res = await apiClient.post(`/referrals/${id}/sign`, {});
    return res.data.data;
  },
  cancel: async (id: string, reason?: string): Promise<Referral> => {
    const res = await apiClient.post(`/referrals/${id}/cancel`, { reason });
    return res.data.data;
  },
  respond: async (id: string, responseNote: string): Promise<Referral> => {
    const res = await apiClient.post(`/referrals/${id}/respond`, { responseNote });
    return res.data.data;
  },
};
